import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

function formatMoney(cents) {
  if (window.Shopify?.formatMoney) {
    return Shopify.formatMoney(cents);
  }
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

function stripHtml(html) {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  const text = (el.textContent || '').trim();
  return text.length > 72 ? `${text.slice(0, 69)}…` : text;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function recommendationCardHtml(product) {
  const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
  if (!variant) return '';

  const title = escapeHtml(product.title);
  const subtitle = stripHtml(product.body_html || product.description || '');
  const subtitleHtml = subtitle
    ? `<p class="otws-cart-rec-card__subtitle">${escapeHtml(subtitle)}</p>`
    : '';

  const img = product.featured_image
    ? `<img src="${product.featured_image}" alt="" class="otws-cart-rec-card__image" width="88" height="88" loading="lazy">`
    : '';

  let priceHtml = `<span class="otws-cart-rec-card__price-sale">${formatMoney(variant.price)}</span>`;
  if (variant.compare_at_price && variant.compare_at_price > variant.price) {
    priceHtml += `<s class="otws-cart-rec-card__price-compare">${formatMoney(variant.compare_at_price)}</s>`;
  }

  return `
    <div class="otws-cart-rec-card" data-product-id="${product.id}">
      <a href="${product.url}" class="otws-cart-rec-card__image-link" tabindex="-1" aria-hidden="true">${img}</a>
      <div class="otws-cart-rec-card__info">
        <a href="${product.url}" class="otws-cart-rec-card__title">${title}</a>
        ${subtitleHtml}
        <div class="otws-cart-rec-card__price">${priceHtml}</div>
        <button type="button" class="otws-cart-rec-card__add" data-otws-add-to-cart data-variant-id="${variant.id}">
          Ajouter au panier
        </button>
      </div>
    </div>
  `;
}

class OtwsCartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.listEl = this.querySelector('[data-otws-cart-recommendations-list]');
    this.loadedForProductId = null;
  }

  connectedCallback() {
    this.load();
    document.addEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
  }

  onCartUpdated = () => {
    const productId = this.dataset.productId;
    if (productId && productId !== this.loadedForProductId) {
      this.load();
    }
  };

  load() {
    const productId = this.dataset.productId;
    const limit = this.dataset.limit || 4;
    const root = window.Shopify?.routes?.root || '/';

    if (!this.listEl || !productId || productId === '0') return;

    const staticCount = this.listEl.querySelectorAll('.otws-cart-rec-card').length;
    if (staticCount >= limit) {
      this.loadedForProductId = productId;
      bindAddToCartButtons(this);
      return;
    }

    // Section Rendering API : rend la section Liquid otws-cart-recommendations,
    // donc les cartes via le snippet otws-cart-recommendation-card (sous-titre =
    // metafield custom.ingredients). La dédup des produits déjà au panier est
    // gérée côté section (Liquid), pas besoin de filtrer ici.
    fetch(
      `${root}recommendations/products?section_id=otws-cart-recommendations&product_id=${productId}&limit=${limit}&intent=related`,
    )
      .then((r) => r.text())
      .then((html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const rootEl = tmp.querySelector('[data-otws-cart-rec-root]') || tmp;

        if (rootEl.querySelector('.otws-cart-rec-card')) {
          this.listEl.innerHTML = rootEl.innerHTML;
          this.loadedForProductId = productId;
          bindAddToCartButtons(this);
        }
      })
      .catch((e) => console.error('OTWS cart recommendations', e));
  }
}

if (!customElements.get('otws-cart-recommendations')) {
  customElements.define('otws-cart-recommendations', OtwsCartRecommendations);
}

function bindAddToCartButtons(root = document) {
  root.querySelectorAll('[data-otws-add-to-cart]').forEach((btn) => {
    if (btn.dataset.otwsBound) return;
    btn.dataset.otwsBound = 'true';

    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variantId;
      const drawer = document.querySelector('cart-drawer[data-section-id]');
      if (!variantId) return;

      btn.disabled = true;
      const prev = btn.textContent;
      btn.textContent = 'Ajout…';

      try {
        const body = {
          items: [{ id: Number(variantId), quantity: 1 }],
        };

        if (drawer?.dataset.sectionId) {
          body.sections = drawer.dataset.sectionId;
          body.sections_url = window.location.pathname;
        }

        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Add failed');

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: data.item_count,
              source: 'otws-cart-recommendations',
              sections: data.sections,
            },
          ),
        );
      } catch (e) {
        console.error(e);
        btn.disabled = false;
        btn.textContent = prev;
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  bindAddToCartButtons();
  document.querySelectorAll('otws-cart-recommendations').forEach((el) => {
    if (el.load) el.load();
  });
});

document.addEventListener(ThemeEvents.cartUpdated, () => {
  bindAddToCartButtons(document.querySelector('cart-drawer') || document);
});
